import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Avatar,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GroupIcon from '@mui/icons-material/Group';
import { useMasterPlayer } from '../../context/MasterPlayerContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import MasterPlayerDialog from '../MasterPlayerDialog/MasterPlayerDialog';
import './MasterPlayerList.scss';

const MasterPlayerList = () => {
  const { 
    masterPlayers, 
    searchTerm, 
    setSearchTerm, 
    getFilteredMasterPlayers,
    deleteMasterPlayer 
  } = useMasterPlayer();
  
  const [expanded, setExpanded] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPlayer, setDeletingPlayer] = useState(null);

  const filteredPlayers = getFilteredMasterPlayers();
  const displayedPlayers = expanded ? filteredPlayers : filteredPlayers.slice(0, 3);

  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (player) => {
    setDeletingPlayer(player);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deletingPlayer) {
      deleteMasterPlayer(deletingPlayer.id);
      setDeleteDialogOpen(false);
      setDeletingPlayer(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Card className="master-player-list">
      <CardContent className="master-player-content">
        <Box className="section-header">
          <Box className="header-info">
            <GroupIcon className="header-icon" />
            <Box>
              <Typography variant="h6" className="section-title">
                Master Player List
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reusable players for all sessions
              </Typography>
            </Box>
          </Box>
          <Box className="header-actions">
            <Chip 
              label={`${masterPlayers.length} players`}
              size="small"
              color={masterPlayers.length > 0 ? "primary" : "default"}
              className="count-chip"
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddDialogOpen(true)}
              className="add-button"
            >
              Add Player
            </Button>
          </Box>
        </Box>

        {masterPlayers.length === 0 ? (
          <Box className="empty-state">
            <GroupIcon className="empty-icon" />
            <Typography variant="h6" className="empty-title">
              No master players yet
            </Typography>
            <Typography variant="body2" color="text.secondary" className="empty-subtitle">
              Create a master list of players you can reuse across sessions
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddDialogOpen(true)}
              className="empty-action-button"
            >
              Add First Player
            </Button>
          </Box>
        ) : (
          <Box className="player-list-section">
            {masterPlayers.length > 3 && (
              <TextField
                fullWidth
                size="small"
                placeholder="Search players..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                className="search-field"
              />
            )}

            <List className="player-list">
              {displayedPlayers.map((player) => (
                <ListItem key={player.id} className="player-item">
                  <ListItemAvatar>
                    <Avatar
                      className="player-avatar"
                      style={{
                        backgroundColor: player.profileColor,
                        color: getContrastTextColor(player.profileColor)
                      }}
                    >
                      {getInitials(player.name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" className="player-name">
                        {player.name}
                      </Typography>
                    }
                    secondary={
                      <Box className="player-details">
                        {player.email && (
                          <Typography variant="caption" className="player-email">
                            {player.email}
                          </Typography>
                        )}
                        <Typography variant="caption" className="player-date">
                          Added {formatDate(player.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction className="player-actions">
                    <IconButton
                      size="small"
                      onClick={() => handleEditPlayer(player)}
                      className="edit-button"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(player)}
                      className="delete-button"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            {filteredPlayers.length > 3 && (
              <Box className="expand-section">
                <Button
                  variant="text"
                  onClick={() => setExpanded(!expanded)}
                  endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  className="expand-button"
                >
                  {expanded ? 'Show Less' : `Show All (${filteredPlayers.length})`}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Add Player Dialog */}
        <MasterPlayerDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
        />

        {/* Edit Player Dialog */}
        <MasterPlayerDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingPlayer(null);
          }}
          editingPlayer={editingPlayer}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Master Player</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{deletingPlayer?.name}" from your master player list? 
              This won't affect any existing session data.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default MasterPlayerList;
